import { useTRPC } from '@/utils/trpc';
import { collectObservationIds } from '@liza/ui/components/records';
import type { DiagnosticReport, Observation, Reference } from '@medplum/fhirtypes';
import { useQueries } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

/**
 * Recursively fetch every Observation referenced from the given reports' `result[]` and any
 * `hasMember[]` chain below them. Returns a stable `Map<id, Observation>` and an `isLoading`
 * flag that stays true until every discovered id is loaded *and* no new ids surfaced from
 * newly-loaded `hasMember` refs.
 *
 * The cumulative state-backed map is what makes the recursive walk work: a render-local
 * `new Map()` would be empty on every pass and the walk would never get past the top level.
 *
 * Takes a list because a resource can link several reports — `Procedure.report[]` is a list,
 * and a lab order renders the single most recent report as an array of one. The `result[]` refs
 * of every report are walked as one id set, so an Observation shared between two reports is
 * fetched once.
 */
export function useDiagnosticReportObservations(diagnosticReports: DiagnosticReport[] | undefined): {
  loadedObservations: Map<string, Observation>;
  isLoading: boolean;
  hasError: boolean;
} {
  const trpc = useTRPC();
  const [loadedObservations, setLoadedObservations] = useState<Map<string, Observation>>(() => new Map());

  const resultRefs = useMemo(
    () => (diagnosticReports ?? []).flatMap((report) => (report.result ?? []) as Reference<Observation>[]),
    [diagnosticReports],
  );

  const observationIds = useMemo(
    () => collectObservationIds(resultRefs, loadedObservations),
    [resultRefs, loadedObservations],
  );

  const observationQueries = useQueries({
    queries: observationIds.map((obsId) => trpc.patient.observation.get.queryOptions({ id: obsId })),
  });

  // Push newly-resolved Observations into the cumulative map. Triggers a re-render whose
  // `collectObservationIds` pass now surfaces deeper `hasMember` refs.
  useEffect(() => {
    setLoadedObservations((prev) => {
      let next: Map<string, Observation> | null = null;
      observationQueries.forEach((q, i) => {
        const obs = q.data?.observation;
        const id = observationIds[i];
        if (!obs || !id || prev.get(id) === obs) return;
        if (!next) next = new Map(prev);
        next.set(id, obs);
      });
      return next ?? prev;
    });
  }, [observationQueries, observationIds]);

  const hasInflight = observationQueries.some((q) => q.isLoading);
  const hasError = observationQueries.some((q) => q.isError);
  // Every id in the current walk must be loaded; otherwise next render will discover more.
  // Skipped when an error has occurred — failed fetches stay missing from `loadedObservations`,
  // so without this short-circuit `hasUndiscovered` would keep `isLoading` true forever.
  const hasUndiscovered = !hasError && observationIds.some((obsId) => !loadedObservations.has(obsId));

  return { loadedObservations, isLoading: hasInflight || hasUndiscovered, hasError };
}
